import { cloneDeep } from 'lodash';
import { useCallback } from 'react';
import { IMemberWebFeatures } from 'types/MemberWebFeatures';
import { TUseActiveMemberUpdateOptions } from '../member/useActiveMemberUpdate';
import useMemberFeatureSettingsUpdate from '../member/useMemberFeatureSettingsUpdate';
import useMemberSettings from '../member/useMemberSettings';

// Feature keys whose settings are an object (i.e. mergeable). Excludes scalar features
// such as `showGpsConnections`, which can't be updated via a `{ key, payload }[]` merge.
type TObjectFeatureKey = {
  [K in keyof IMemberWebFeatures]-?: NonNullable<IMemberWebFeatures[K]> extends object ? K : never;
}[keyof IMemberWebFeatures];

type TFeatureSettings<F extends TObjectFeatureKey> = NonNullable<IMemberWebFeatures[F]>;

/**
 * Builds an updater for a single web feature's member settings.
 *
 * The returned callback takes an array of `{ key, payload }` changes, merges them into the
 * feature's existing settings, and persists the result. This is the shared implementation
 * behind the per-feature `useXSettingsUpdate` hooks — each of those is now just
 * `useFeatureSettingsUpdate('<featureKey>')`, so the clone/merge logic lives in one place
 * and the settings type is derived from the feature key (no separate type import needed).
 */
export default function useFeatureSettingsUpdate<F extends TObjectFeatureKey>(feature: F) {
  const memberSettings = useMemberSettings();
  const updateFeatureSettings = useMemberFeatureSettingsUpdate();
  const featureSettings = memberSettings?.web?.features?.[feature];

  return useCallback(
    <K extends keyof TFeatureSettings<F>>(
      keysAndPayloads: { key: K; payload: TFeatureSettings<F>[K] }[],
      options?: TUseActiveMemberUpdateOptions
    ) => {
      const cloned = cloneDeep(featureSettings);

      // No changes: persist the existing settings unchanged (matches prior behavior).
      if (keysAndPayloads.length === 0) {
        return updateFeatureSettings(feature, cloned as IMemberWebFeatures[F], options);
      }

      const mapped: Record<PropertyKey, unknown> = { ...cloned };
      keysAndPayloads.forEach(({ key, payload }) => {
        mapped[key] = payload;
      });

      return updateFeatureSettings(feature, mapped as IMemberWebFeatures[F], options);
    },
    [featureSettings, updateFeatureSettings, feature]
  );
}
